import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Typography,
    Container,
    IconButton,
    Alert,
} from '@mui/material';
import {
    Phone as PhoneIcon,
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { colors, typography } from '../theme/tokens';
import { DEMO_MODE, DEMO_CREDENTIALS, verifyDemoOTP, sendDemoOTP } from '../utils/demoAuth';
import { loginSuccess, setTakeawayList } from '../redux/slices/authSlice';
import { verifyOTPAPI, sendOTPAPI } from '../services/authService';
import authBgImage from '../assets/images/auth-bg.png';
import Logo from '../assets/images/digilogo-w.svg';
import { RiArrowRightCircleFill, RiPhoneLine } from '@remixicon/react';

const OTPVerification = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const registerNumber = location.state?.registerNumber || '';
    const countryCode = location.state?.countryCode || '+44';
    const otpLength = location.state?.otpLength || 6; // Get OTP length from navigation state, default to 6
    
    // Initialize OTP array dynamically based on otpLength
    const [otp, setOtp] = useState(() => Array(otpLength).fill(''));
    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const inputRefs = useRef([]);

    // Timer countdown
    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [timer]);

    // Auto-verify when all digits are entered
    useEffect(() => {
        if (otp.every((digit) => digit !== '')) {
            handleVerifyOTP();
        }
    }, [otp]);

    const handleOtpChange = (index, value) => {
        // Only allow digits
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1); // Take only last character
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < otpLength - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, otpLength);
        if (!/^\d+$/.test(pastedData)) return;

        const newOtp = [...otp];
        pastedData.split('').forEach((char, index) => {
            if (index < otpLength) newOtp[index] = char;
        });
        setOtp(newOtp);

        // Focus the next empty input or the last one
        const nextEmptyIndex = newOtp.findIndex((digit) => !digit);
        const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : otpLength - 1;
        inputRefs.current[focusIndex]?.focus();
    };

    const handleVerifyOTP = async () => {
        const otpCode = otp.join('');
        if (otpCode.length !== otpLength) return;

        setIsVerifying(true);
        try {
            if (false) {
                // Demo mode verification
                const result = await verifyDemoOTP(otpCode);
                dispatch(loginSuccess(result.user));
                navigate('/');
            } else {
                // Production mode - API OTP verification
                const result = await verifyOTPAPI(registerNumber, otpCode,'united kingdom', 'English (United Kingdom)');
                
                if (result.success && result.data && result.data.length > 0) {
                    // Store the takeaway list (branches) in Redux
                    dispatch(setTakeawayList(result.data));
                    
                    // Store the first takeaway/store data as user
                    const userData = result.data[0];
                    dispatch(loginSuccess(userData));
                    
                    // On success, navigate to dashboard
                    navigate('/');
                } else {
                    throw new Error(result.error || result.data?.message || 'Invalid OTP code');
                }
            }
        } catch (error) {
            console.error('OTP verification error:', error);
            alert(error.message || 'Invalid OTP code. Please try again.');
            // Clear OTP on error - reset to array of empty strings with correct length
            setOtp(Array(otpLength).fill(''));
            inputRefs.current[0]?.focus();
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResendCode = async () => {
        if (!canResend) return;

        try {
            if (DEMO_MODE) {
                await sendDemoOTP(registerNumber);
            } else {
                // Resend OTP via API - registerNumber already includes country code from login
                const result = await sendOTPAPI(registerNumber, 'sms', 'united kingdom', 'English (United Kingdom)');
                
                if (!result.success) {
                    throw new Error(result.error || result.data?.message || 'Failed to resend OTP');
                }
            }

            setTimer(60);
            setCanResend(false);
            setOtp(Array(otpLength).fill(''));
            inputRefs.current[0]?.focus();
            alert('OTP code resent successfully!');
        } catch (error) {
            console.error('Resend code error:', error);
            alert(error.message || 'Error resending OTP. Please try again.');
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                bgcolor: colors.background.default,
            }}
        >
            {/* Left Side - Branding */}
            <Box
                sx={{
                    flex: 1,
                    backgroundImage: `url(${authBgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    position: 'relative',
                    display: { xs: 'none', md: 'flex' },
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: 6,
                    color: 'white',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        // background: 'rgba(0, 0, 0, 0.2)',
                        zIndex: 0,
                    },
                    '& > *': {
                        position: 'relative',
                        zIndex: 1,
                    },
                    margin: 1,
                    borderRadius: 2,
                }}
            >
                {/* Logo and Tagline */}
                <Box sx={{ textAlign: 'center' }}>
                    {/* <Box
            sx={{
              width: 200,
              height: 200,
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 6,
              mx: 'auto',
              backdropFilter: 'blur(10px)',
              border: '3px solid rgba(255,255,255,0.2)',
            }}
          >
            <Box
              sx={{
                width: 140,
                height: 140,
                bgcolor: 'white',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '4rem',
                  fontWeight: 'bold',
                  color: colors.primary.main,
                }}
              >
                S
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="h4"
            sx={{
              fontWeight: typography.fontWeightBold,
              mb: 1,
            }}
          >
            Digi Solutions
          </Typography> */}
                    <Box sx={{ mb: 6, textAlign: 'center' }}>
                        <img src={Logo} alt="Logo" />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontFamily: typography.fontFamilyHeading,
                            mb: 3,
                            fontSize: { xs: '2rem', md: '3rem' },
                            background: 'linear-gradient(90deg, rgba(246, 246, 246, 0.70) 8.63%, #FDEEF0 44.87%, rgba(255, 253, 253, 0.70) 109.13%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}
                    >
                        Don't Say It, Display It!
                    </Typography>
                </Box>

                {/* Back to Login Button */}
                <Button
                    onClick={() => navigate('/login')}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                        position: 'absolute',
                        bottom: 32,
                        right: 32,
                        bgcolor: colors.neutral.white,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: typography.fontWeightSemiBold,
                        textTransform: 'none',
                        fontFamily: typography.fontFamily,
                        color: '#050507',
                        fontSize: '1rem',
                        '&:hover': {
                            bgcolor: colors.neutral.white
                        },
                    }}
                >
                    Back To Login
                </Button>
            </Box>

            {/* Right Side - OTP Form */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { xs: 3, md: 6 },
                    bgcolor: 'white',
                }}
            >
                <Container maxWidth="sm">
                    <Box sx={{
                        p: 6,
                        mb: 6,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: '2 solid #EFEFF0',
                        bgcolor: '#FDFDFD',
                        boxShadow: '0 24px 50px 0 rgba(0, 0, 0, 0.10)'
                    }}>
                        <Box sx={{ mb: 6, textAlign: 'center' }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: typography.fontWeightBold,
                                    color: colors.neutral.gray900,
                                    mb: 2,
                                }}
                            >
                                Enter The OTP
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: colors.neutral.gray700,
                                    fontSize: '1rem',
                                }}
                            >
                                Enter The {otpLength}-Digit Code Sent To Your Phone Number
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: colors.neutral.gray700,
                                    fontSize: '1rem',
                                    mb: 3,
                                }}
                            >
                                To Complete Verification
                            </Typography>

                            {/* {DEMO_MODE && (
                                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        Demo Mode Active
                                    </Typography>
                                    <Typography variant="caption">
                                        Use OTP: <strong>{DEMO_CREDENTIALS.otp}</strong>
                                    </Typography>
                                </Alert>
                            )} */}
                        </Box>

                        {/* OTP Input Boxes */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 2,
                                mb: 6,
                            }}
                            onPaste={handlePaste}
                        >
                            {otp.map((digit, index) => (
                                <Box
                                    key={index}
                                    component="input"
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    disabled={isVerifying}
                                    sx={{
                                        width: { xs: 50, sm: 70 },
                                        height: { xs: 50, sm: 70 },
                                        fontSize: '1.75rem',
                                        fontWeight: typography.fontWeightBold,
                                        textAlign: 'center',
                                        border: `2px solid ${digit ? '#3D3C4A' : '#EFEFF0'
                                            }`,
                                        borderRadius: 1.5,
                                        outline: 'none',
                                        transition: 'all 0.2s ease',
                                        bgcolor: '#FDFDFD',
                                        color: colors.neutral.gray900,
                                        '&:focus': {
                                            borderColor: '#EFEFF0',
                                            border: `1px solid '#EFEFF0'`,
                                            boxShadow: `0 0 0 4px rgba(5, 5, 7, 0.20)`,
                                        },
                                        '&:disabled': {
                                            opacity: 0.6,
                                            cursor: 'not-allowed',
                                        },
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Resend Code */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                                mb: 8,
                            }}
                        >
                            <IconButton
                                onClick={handleResendCode}
                                disabled={!canResend}
                                sx={{
                                    color: canResend ? colors.primary.main : colors.neutral.gray400,
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: canResend ? colors.primary.main : colors.neutral.gray600,
                                    fontWeight: typography.fontWeightMedium,
                                    cursor: canResend ? 'pointer' : 'default',
                                }}
                                onClick={handleResendCode}
                            >
                                Resend code
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: colors.neutral.gray600,
                                    fontWeight: typography.fontWeightMedium,
                                    minWidth: 45,
                                }}
                            >
                                {formatTime(timer)}
                            </Typography>
                        </Box>
                    </Box>
                    {/* Header */}

                    {/* Support Section */}
                    <Box
                        sx={{
                            mt: 6,
                            p: 3,
                            bgcolor: 'linear-gradient(180deg, rgba(249, 209, 213, 0.05) 0%, rgba(226, 209, 249, 0.05) 100%)',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border: '1px solid #C61F2F'
                        }}
                    >
                        <Box sx={{ alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RiPhoneLine size={24} color={colors.primary.main} />
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: typography.fontWeightSemiBold,
                                        color: colors.neutral.gray900,
                                    }}
                                >
                                    24/7 Support
                                </Typography>
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: colors.neutral.gray600,
                                    fontSize: '0.85rem',
                                }}
                            >
                                Always-on help by chat and email.
                            </Typography>
                        </Box>
                        <RiArrowRightCircleFill size={24} color={colors.primary.main} />
                    </Box>

                    {/* Mobile Back Button */}
                    <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 4 }}>
                        <Button
                            fullWidth
                            onClick={() => navigate('/login')}
                            startIcon={<ArrowBackIcon />}
                            sx={{
                                color: colors.primary.main,
                                borderColor: colors.primary.main,
                                py: 1.5,
                                borderRadius: 2,
                                fontWeight: typography.fontWeightSemiBold,
                                textTransform: 'none',
                                fontSize: '1rem',
                            }}
                            variant="outlined"
                        >
                            Back To Login
                        </Button>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default OTPVerification;

